// import { logDebugging } from '../log/logger';
import PlayerFactory from './players/player.factory';
import * as STATES from './states/player.states';
import * as VOLUME_STATES from './states/volume.states';
import * as AdEvents from './events/ad.events';
import PlayerEvent from './events/player.event';
import { logDebugging } from '../log/logger';
import StatsManager from './stats/statsManager';
// eslint-disable-next-line import/no-named-as-default
import midroll from '../utils/midroll';
import overlay from '../utils/overlay';

export default class VideoPlayer {
  constructor(videoData, config) {
    this.videoData = videoData;
    this.config = config;
    this.playerEvent = new PlayerEvent('onVideoPlayerEvent');
    this.stateEvent = new PlayerEvent('onVideoStateEvent');
    this.state = STATES.STANDBY;
    this.muted = true;
    this.currentTime = 0;
    this.duration = 0;
    this.statsManager = new StatsManager(videoData);
    this.activatedListeners = false;
    this.canPlayReached = false;
  }

  loadVideo() {
    midroll.reset();
    overlay.reset();
    this.playerFactory = new PlayerFactory(this.videoData, this.config).getType(
      this.videoData.typeFactory,
    );
    this.playerFactory.load().then((playerController) => {
      if (playerController) {
        this.playerController = playerController;

        if (!this.activatedListeners) {
          this.playerFactory.getEvent().subscribe((sender, message) => {
            this.activatedListeners = true;
            const oldState = this.state;
            const oldVolumeState = this.volumeState;
            // nota: tenemos remaingTime como cuarto parametro
            const [adEvent, currentTime, duration] = message.message;
            // logDebugging('[VideoPlayer] adEvent ', adEvent, currentTime);
            this.statsManager.sendEvents(adEvent);
            switch (adEvent) {
              case AdEvents.AdCanplay:
                // para arrancar en el iphone cuando el player ya está cargado.
                // comprobamos que no está reproduciendose ya para arrancarlo.
                setTimeout(() => {
                  if (
                    this.state === STATES.STANDBY &&
                    this.config.autoplay &&
                    !this.canPlayReached
                  ) {
                    this.start();
                    this.canPlayReached = true;
                  }
                }, 500);
                break;
              case AdEvents.AdLoaded:
                logDebugging('[VideoPlayer] ', 'THROW_PREROLL');
                this.dispatchEvent([this.playerEvent.THROW_PREROLL, currentTime, duration]);
                break;
              case AdEvents.AdStarted:
                this.state = STATES.PLAYING;
                this.dispatchEvent([AdEvents.AdVideoStart, currentTime, duration]);
                break;
              case AdEvents.AdPlaying:
                this.state = STATES.PLAYING;
                break;
              case AdEvents.AdPaused:
                this.state = STATES.PAUSED;
                break;
              case AdEvents.AdError:
                this.state = STATES.ERROR;
                break;
              case AdEvents.AdStopped:
                this.state = STATES.STOPPED;
                this.dispatchEvent([AdEvents.AdVideoComplete, currentTime, duration]);
                break;
              case AdEvents.AdSkipped:
                this.state = STATES.SKIPPED;
                break;
              case AdEvents.AdReplay:
                this.state = STATES.REPLAY;
                break;
              case AdEvents.AdMute:
                this.volumeState = VOLUME_STATES.MUTED;
                break;
              case AdEvents.AdUnmute:
                this.volumeState = VOLUME_STATES.UNMUTED;
                break;
              case AdEvents.AdVolumeChange:
                this.dispatchEvent([this.playerEvent.VOLUME_EVENT, message.message[1]]);
                break;
              case AdEvents.AdRemainingTimeChange:
                this.currentTime = currentTime;
                this.duration = duration;
                if (currentTime >= duration - 10 && !this.postrollReached) {
                  this.postrollReached = true;
                  logDebugging('[VideoPlayer] ', 'THROW_POSROLL');
                  this.dispatchEvent([this.playerEvent.THROW_POSROLL, currentTime, duration]);
                }
                if (midroll.isLastMidroll() && overlay.isLastOverlay()) {
                  // logDebugging('[VideoPlayer] ', 'midroll.isLastMidroll()');
                  return;
                }
                if (
                  !midroll.isLastMidroll() &&
                  midroll.checkIfThrowMidroll(Math.floor((currentTime * 100) / duration))
                ) {
                  logDebugging('[VideoPlayer] ', 'disparo midroll');
                  this.dispatchEvent([this.playerEvent.THROW_MIDROLL, currentTime, duration]);
                }
                if (
                  !overlay.isLastOverlay() &&
                  overlay.checkIfThrowOverlay(Math.floor((currentTime * 100) / duration))
                ) {
                  logDebugging('[VideoPlayer] ', 'disparo overlay');
                  this.dispatchEvent([this.playerEvent.THROW_OVERLAY, currentTime, duration]);
                }
                break;
              case AdEvents.AdDurationChange:
                this.duration = duration;
                // logDebugging('duration ', duration);
                break;

              case AdEvents.AdVideoFirstQuartile:
              case AdEvents.AdVideoMidpoint:
              case AdEvents.AdVideoThirdQuartile:
                this.duration = duration;
                this.dispatchEvent([adEvent, currentTime, duration]);
                // logDebugging('duration ', duration);
                break;

              case AdEvents.AdPlayError:
                this.state = STATES.STANDBY;
                this.dispatchEvent([adEvent, currentTime, duration]);
                break;
              default:
                break;
            }
            if (this.state !== oldState) {
              this.dispatchEvent([this.playerEvent.STATE_EVENT, this.state]);
              this.dispatchStateEvent([this.playerEvent.STATE_EVENT, this.state]);
            }
            if (this.volumeState !== oldVolumeState) {
              this.dispatchEvent([this.playerEvent.VOLUME_EVENT, this.volumeState]);
            }
          });
        }

        this.playerController.initialize();
      }
    });
  }

  start(force) {
    this.playerController?.mute();
    if (!force && this.config.numAdsPre) this.dispatchEvent([this.playerEvent.INIT_PREROLL]);
    else this.playerController?.start(force);
  }

  remove() {
    this.playerController?.remove();
  }

  pause() {
    this.changeState(STATES.PAUSED);
    this.playerController?.pause();
  }

  resume() {
    this.changeState(STATES.PLAYING);
    this.playerController?.resume();
  }

  seekTo(percent) {
    this.playerController?.seekTo(percent);
  }

  skip() {
    this.playerController?.skip();
  }

  replay() {
    this.playerController?.replay();
  }

  getVolume() {
    return this.playerController?.getVolume();
  }

  setVolume(value) {
    this.playerController?.setVolume(value);
  }

  mute() {
    this.playerController?.mute();
    this.muted = true;
  }

  unmute() {
    this.playerController?.unmute();
    this.muted = false;
  }

  getDuration() {
    return this.playerController?.getDuration();
  }

  getCurrentTime() {
    return this.playerController?.getCurrentTime();
  }

  getRemainingTime() {
    return this.playerController?.getRemainingTime();
  }

  resize() {
    this.playerController?.resize();
  }

  changeState(newState) {
    this.state = newState;
    this.dispatchStateEvent([this.STATE_EVENT, newState]);
    this.dispatchEvent([this.playerEvent.STATE_EVENT, newState]);
  }

  dispatchEvent(arg) {
    this.playerEvent.fire(this, { message: arg });
  }

  dispatchStateEvent(arg) {
    this.stateEvent.fire(this, { message: arg });
  }

  unsubscribe() {
    this.playerFactory.getEvent().unsubscribe();
  }
}
